import type {
  FieldError,
  FieldValues,
  UnpackNestedValue,
} from 'react-hook-form';

type ValidationErrors = Record<string, string[] | undefined>;

type ResolverSuccess<T> = { values: T; errors: Record<string, never> };
type ResolverError = {
  values: Record<string, never>;
  errors: Record<string, FieldError>;
};
type ResolverResultCustom<T> = ResolverSuccess<T> | ResolverError;

/**
 * Wraps existing validation functions that return Record<string, string[] | undefined>
 * into react-hook-form Resolver format.
 *
 * This allows reusing existing validation logic (e.g., validateRegion, validateModel)
 * with react-hook-form without rewriting validation rules.
 *
 * @example
 * const regionResolver = createValidationResolver(validateRegion);
 * const { control, handleSubmit } = useForm({ resolver: regionResolver });
 */
export function createValidationResolver<TFormValues extends FieldValues>(
  validateFn: (
    values: UnpackNestedValue<TFormValues>,
  ) => Promise<ValidationErrors>,
) {
  return async (
    values: UnpackNestedValue<TFormValues>,
    _context: unknown,
    _options: unknown,
  ): Promise<ResolverResultCustom<UnpackNestedValue<TFormValues>>> => {
    const validationErrors = await validateFn(values);

    const errors: Record<string, FieldError> = {};

    for (const key of Object.keys(validationErrors)) {
      const messages = validationErrors[key];
      if (messages && messages.length > 0 && key in values) {
        errors[key] = {
          type: 'validation',
          message: messages[0],
        };
      }
    }

    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      return { values: {}, errors };
    }

    return { values, errors: {} };
  };
}

export default createValidationResolver;
